import { useState, type FormEvent } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import useDocumentMeta from '@/hooks/useDocumentMeta';

const FORM_URL = 'https://readdy.ai/api/form/db4nk16k6jkc2oct49b0';

const contactCards = [
  {
    icon: 'ri-phone-line',
    label: '客服热线',
    value: '400-91-91-091',
    desc: '周一至周日 09:00 - 21:00',
  },
  {
    icon: 'ri-mail-line',
    label: '电子邮箱',
    value: 'service@91portal.cn',
    desc: '商务合作：bd@91portal.cn',
  },
  {
    icon: 'ri-map-pin-line',
    label: '办公地址',
    value: '北京市朝阳区门户大厦 91 号',
    desc: '邮编 100020 · 欢迎预约来访',
  },
  {
    icon: 'ri-time-line',
    label: '服务时间',
    value: '全年无休',
    desc: '紧急问题可通过邮箱反馈',
  },
];

export default function ContactPage() {
  useDocumentMeta({
    title: '联系我们 - 91网站',
    description:
      '联系91网站：客服热线、电子邮箱与办公地址一览。欢迎通过意见反馈表单留下你的建议，我们将认真对待每一条反馈。',
    keywords: '91网站,联系我们,意见反馈,客服热线,商务合作',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new FormData();
    (['name', 'contact', 'message'] as const).forEach((key) => {
      const value = fd.get(key);
      if (typeof value === 'string' && value.trim()) {
        payload.append(key, value.trim());
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;

      if (!response.ok || code !== 'OK' || /spam/i.test(String(serverMsg))) {
        setStatus('error');
        setFormError(String(serverMsg) || '提交失败，请稍后重试');
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试');
    }
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb items={[{ label: '联系我们' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-secondary-600">
            无论是内容反馈、服务咨询还是商务合作，欢迎随时与我们取得联系。你的每一条建议，都是我们前进的动力。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {contactCards.map((c) => (
            <div key={c.label} className="rounded-xl border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <i className={`${c.icon} text-xl`}></i>
              </span>
              <div className="mt-3 text-xs text-secondary-500">{c.label}</div>
              <div className="mt-1 text-sm font-semibold text-foreground-950">{c.value}</div>
              <div className="mt-1 text-xs text-secondary-400">{c.desc}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-feedback-line text-accent-500"></i>
              意见反馈
            </h2>
            <p className="mt-2 text-xs text-secondary-500">
              填写下方表单，将你的意见或建议告诉我们，我们会尽快跟进处理。
            </p>

            {status === 'success' ? (
              <div className="mt-8 flex flex-col items-center rounded-lg border border-accent-200 bg-accent-50 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-check-line text-3xl"></i>
                </span>
                <p className="mt-4 font-medium text-foreground-950">反馈已提交，感谢你的宝贵意见</p>
                <p className="mt-1 text-xs text-secondary-500">我们会认真阅读并跟进，期待与你共同成长</p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  再填写一条
                </button>
              </div>
            ) : (
              <form
                id="db4nk16k6jkc2oct49b0"
                data-readdy-form
                onSubmit={handleSubmit}
                className="relative mt-6 space-y-4"
              >
                <div>
                  <label htmlFor="cf-name" className="mb-1.5 block text-xs text-secondary-600">
                    姓名 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的姓名"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="cf-contact" className="mb-1.5 block text-xs text-secondary-600">
                    联系方式 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="cf-contact"
                    name="contact"
                    type="text"
                    required
                    placeholder="手机号 / 微信 / 邮箱"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="cf-message" className="mb-1.5 block text-xs text-secondary-600">
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="cf-message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    placeholder="请描述你的问题、建议或合作意向（不超过 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  />
                </div>

                <input
                  className="form-field-website-alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === 'error' && formError && (
                  <p className="flex items-center gap-1.5 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交反馈
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
            <div className="border-b border-background-200 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-map-2-line text-accent-500"></i>
                到访路线
              </h2>
              <p className="mt-2 text-xs text-secondary-500">
                北京市朝阳区门户大厦 91 号 · 地铁 6 号线朝阳门站步行约 8 分钟
              </p>
            </div>
            <iframe
              title="91网站办公地址地图"
              aria-label="91网站办公地址地图"
              src="https://singular.net.cn/maps?q=Beijing%20Chaoyang%20district&output=embed"
              className="h-72 w-full md:h-[360px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}